import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  LinearProgress,
  Slide,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import UserSearch from '../../../../shared-components/UserSearch';
import UserService from '../../../../shared-services/User/UserService';
import { ICoreUser } from '../../../../shared-interfaces/User/ICoreUser';
import { IRetentionStatus } from '../../../../shared-interfaces/User/IRetentionStatus';
import { IUserRetentionStatus } from '../../../../shared-interfaces/User/IUserRetentionStatus';
import { Inclusion } from '../../../../shared-types/Inclusion';
import { Role } from '../../../../shared-types/Roles';
import { GetUserRoles } from '../Functions/GetUserRoles';
import { IUserManagementFilter } from '../Interfaces/IUserManagementFilter';
import { IUserRole } from '../Interfaces/IUserRole';
import UserManagementService from '../Services/UserManagementService';
import { UserManagementActionType } from '../Types/UserManagementActionType';
import { UserManagementDispatch, UserManagementState } from '../UserManagement';
import { ChangeRetentionDialogContent } from './ChangeRetentionStatusDialog';
import { ChangeRoleDialogContent } from './ChangeRoleDialog';
import { UserAttributesContext } from '../../../../shared-components/contexts/UserAttributesContext';

export interface IActionStepperDialog {
  open: boolean;
  close: () => void;
}

const ActionStepperDialog = (props: IActionStepperDialog) => {
  const state = useContext(UserManagementState);
  const dispatch = useContext(UserManagementDispatch);
  const userAttributeContext = useContext(UserAttributesContext);
  const { enqueueSnackbar } = useSnackbar();

  const [users, setUsers] = useState<ICoreUser[]>([]);
  const [userRetentionStatuses, setUserRetentionStatuses] =
    useState<IUserRetentionStatus[]>();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const roles = GetUserRoles();
  const [role, setRole] = useState<IUserRole>(roles[0]);

  const [retentionStatus, setRetentionStatus] = useState<IRetentionStatus>(
    userAttributeContext.retentionStatuses[0],
  );
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(new Date());

  useEffect(() => {
    setUsers([] as ICoreUser[]);
    setCurrentStep(0);
  }, [props.open]);

  useEffect(() => {
    if (users.length > 0) {
      UserService.getUsersCurrentRetentionStatus(users.map(x => x.id)).then(
        r => {
          setUserRetentionStatuses(r);
        },
      );
    }
  }, [users]);

  const handleRoleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setRole(roles.find(x => x.value === event.target.value) as IUserRole);
  };

  const handleRetentionChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setRetentionStatus(
      userAttributeContext.retentionStatuses.find(
        x => x.id === event.target.value,
      ) as IRetentionStatus,
    );
  };

  const handleUserClear = (user: ICoreUser) => {
    setUsers(users.filter(x => x.id !== user.id));
  };

  const handleSubmit = () => {
    setIsSubmitting(true);

    if (currentStep === 1) {
      // Change Role
      UserManagementService.bulkUpdateUserRole(
        role.value,
        users.map(x => x.id),
        Inclusion.Include,
        {} as IUserManagementFilter,
      )
        .then(() => {
          dispatch({
            type: UserManagementActionType.UpdateUsers,
            value: users,
          });

          enqueueSnackbar('Successfully Changed Roles', {
            variant: 'success',
          });
          props.close();
        })
        .catch(e => {
          enqueueSnackbar('Failed to Change Roles', {
            variant: 'error',
          });
        })
        .finally(() => setIsSubmitting(false));
    } else if (currentStep === 2) {
      // Change Retention Status
      UserManagementService.bulkUpdateUserRetention(
        retentionStatus?.id ?? 0,
        users
          .filter(x => x.role === Role.Student || x.role === Role.PeerAdvisor)
          .map(x => x.id),
        Inclusion.Include,
        effectiveDate ?? new Date(),
        {} as IUserManagementFilter,
      )
        .then(x => {
          dispatch({
            type: UserManagementActionType.SetUsers,
            users: state.users,
          });
          enqueueSnackbar('Successfully Updated Retention Status', {
            variant: 'success',
          });
          props.close();
        })
        .catch(x => {
          enqueueSnackbar('Failed to Update Retention Status', {
            variant: 'error',
          });
        })
        .finally(() => setIsSubmitting(false));
    } else {
      enqueueSnackbar('Something went wrong. Please try again', {
        variant: 'error',
      });
    }
  };

  const ChangeRoleStep = () => {
    return (
      <ChangeRoleDialogContent
        users={users}
        role={role}
        onRoleChange={e => handleRoleChange(e)}
        onUserClear={u => handleUserClear(u)}
        entryMethod="BulkOps"
      />
    );
  };

  const ChangeRetentionStep = () => {
    return users.findIndex(
      x => x.role === Role.Student || x.role === Role.PeerAdvisor,
    ) > -1 ? (
      <ChangeRetentionDialogContent
        users={users}
        userRetentionStatuses={userRetentionStatuses}
        effectiveDate={effectiveDate}
        retentionStatus={retentionStatus}
        onRetentionChange={e => handleRetentionChange(e)}
        onEffectiveDateChange={d => setEffectiveDate(d)}
        onUserClear={u => handleUserClear(u)}
        entryMethod="BulkOps"
      />
    ) : (
      <Box
        height="200px"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Typography variant="h2">
          No applicable users selected. <br />
          Retention Status changes can only be applied to Students and Peer
          Advisors.
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={props.open}
      keepMounted
      onClose={props.close}
      aria-labelledby="select-multiple-users-dialog-title"
      aria-describedby="select-multiple-users-dialog-description"
      maxWidth="md"
      fullWidth={true}
    >
      <DialogTitle id="select-multiple-users-dialog-title">
        {currentStep === 0
          ? 'Select Multiple Users'
          : currentStep === 1
          ? 'Change Role'
          : 'Change Retention Status'}
      </DialogTitle>
      <DialogContent style={{ overflowX: 'hidden' }}>
        {/* TODO: Make these animations smoother. Setting position: absolute helps, but causes some ugly height issues */}
        <Slide
          in={currentStep === 0}
          direction="right"
          timeout={users.length === 0 ? 0 : 500}
          mountOnEnter
          unmountOnExit
        >
          {/* Autocomplete breaks when we split this out into its own component */}
          <div>
            <UserSearch
              label="Name or External ID(s) of User(s)"
              multiple
              value={users}
              onSelect={v => {
                setUsers(v as ICoreUser[]);
              }}
            />
            <FormHelperText id="select-multiple-users-dialog-description">
              Search and select multiple users. Paste a comma seperated list of
              External Ids to quickly select several users.
            </FormHelperText>
          </div>
        </Slide>
        <Slide
          in={currentStep === 1}
          direction="left"
          timeout={500}
          mountOnEnter
          unmountOnExit
        >
          <div>
            <ChangeRoleStep />
          </div>
        </Slide>
        <Slide
          in={currentStep === 2}
          direction="left"
          timeout={500}
          mountOnEnter
          unmountOnExit
        >
          <div>
            <ChangeRetentionStep />
          </div>
        </Slide>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={() => props.close()} color="secondary">
          Close
        </Button>
        {currentStep > 0 && (
          <Button onClick={() => setCurrentStep(0)} color="primary">
            Back
          </Button>
        )}
        {currentStep === 0 ? (
          <>
            <Button
              disabled={users.length === 0}
              onClick={() => setCurrentStep(1)}
              color="primary"
            >
              Change Role
            </Button>
            <Button
              disabled={users.length === 0}
              onClick={() => setCurrentStep(2)}
              color="primary"
            >
              Change Retention Status
            </Button>
          </>
        ) : (
          <Button
            disabled={users.length === 0}
            onClick={handleSubmit}
            color="primary"
          >
            Confirm
          </Button>
        )}
      </DialogActions>
      {isSubmitting && <LinearProgress />}
    </Dialog>
  );
};

export default ActionStepperDialog;
